import { useMemo, useState } from "react";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";

import { engineLabel, type GeoProbe, type GeoReport } from "../../../api/geoService";

const STAGE_LABELS: Record<string, string> = {
  awareness: "认知",
  compare: "对比",
  decide: "决策",
  doubt: "疑虑",
};

const PAGE = 9;

/** 一条探针是否「扎心」:出现负面,或竞对被推荐而你缺席。 */
function isAlert(p: GeoProbe): boolean {
  if (p.sentiment === "negative") return true;
  return !p.mentioned && (p.competitors?.length ?? 0) > 0;
}

function ProbeCard({ probe }: { probe: GeoProbe }) {
  const alert = isAlert(probe);
  return (
    <div
      data-testid="geo-probe-card"
      className={`flex flex-col gap-2 rounded-lg border p-3 text-sm ${
        alert ? "border-rose-500/50 bg-rose-500/5" : "border-border bg-muted/30"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 font-semibold">
          {engineLabel(probe.platform)}
          {probe.mode === "search" && (
            <Badge variant="outline" className="border-primary/50 text-primary">
              联网检索
            </Badge>
          )}
        </span>
        <span className="text-xs text-muted-foreground">
          {STAGE_LABELS[probe.stage] ?? probe.stage}
          {probe.intent ? ` · ${probe.intent}` : ""}
        </span>
      </div>
      <div className="text-muted-foreground">「{probe.question}」</div>
      {probe.answer_excerpt && (
        <div className="rounded bg-background/60 p-2 text-xs leading-relaxed">
          {probe.answer_excerpt}
          {probe.answer_full && probe.answer_full.length > (probe.answer_excerpt?.length ?? 0) && (
            <details className="mt-1">
              <summary className="cursor-pointer select-none text-primary">查看原文</summary>
              <div className="mt-1 whitespace-pre-wrap text-muted-foreground">
                {probe.answer_full}
              </div>
            </details>
          )}
        </div>
      )}
      {(probe.citations ?? []).length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid="geo-probe-citations">
          {(probe.citations ?? []).slice(0, 5).map((c, i) => (
            <span
              key={i}
              className="rounded-full border border-primary/40 bg-primary/5 px-2 py-0.5 text-xs text-muted-foreground"
              title={c.title}
            >
              引用 {c.domain ?? c.media}
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {probe.mentioned ? (
          <Badge variant="outline" className="border-emerald-500/50 text-emerald-500">
            提及{typeof probe.rank === "number" ? ` · 第${probe.rank}位` : ""}
          </Badge>
        ) : (
          <Badge variant="outline" className="border-rose-500/50 text-rose-500">
            未提及
          </Badge>
        )}
        {probe.is_recommended && (
          <Badge variant="outline" className="border-emerald-500/50 text-emerald-500">
            被推荐
          </Badge>
        )}
        {probe.sentiment === "negative" && (
          <Badge variant="outline" className="border-rose-500/50 text-rose-500">
            负面
          </Badge>
        )}
        {(probe.competitors?.length ?? 0) > 0 && (
          <Badge
            variant="outline"
            className={
              probe.mentioned
                ? "border-border font-normal text-muted-foreground"
                : "border-amber-500/50 text-amber-500"
            }
          >
            {probe.mentioned ? "同屏竞品:" : "被推荐的是:"}
            {probe.competitors!.slice(0, 3).join("、")}
            {probe.competitors!.length > 3 ? ` 等${probe.competitors!.length}家` : ""}
          </Badge>
        )}
        {(probe.reasons ?? []).slice(0, 2).map((r) => (
          <Badge key={r} variant="secondary" className="font-normal">
            {r}
          </Badge>
        ))}
        {(probe.weaknesses ?? []).slice(0, 2).map((w) => (
          <Badge
            key={w}
            variant="outline"
            className="border-amber-500/50 font-normal text-amber-500"
          >
            短板:{w}
          </Badge>
        ))}
      </div>
    </div>
  );
}

/** AI 原话回放墙:用户真的这么问,AI 真的这么答;支持按引擎/阶段筛选,扎心卡置顶。 */
export function ProbeWall({ report }: { report: GeoReport }) {
  // S1:推演探针无原话,不进回放墙 —— 原话只收实测(诚实红线)。
  // codex S1 P2:provenance 是真伪判定唯一真源,mode 只是采集通道 → 两个都查(双保险)。
  const probes = useMemo(
    () => (report.probes ?? []).filter((p) => p.mode !== "derived" && p.provenance !== "derived"),
    [report.probes]
  );
  const [stage, setStage] = useState<string>("all");
  const [platform, setPlatform] = useState<string>("all");
  const [limit, setLimit] = useState(PAGE);
  const [view, setView] = useState<"cards" | "table">("cards");

  const platforms = useMemo(() => [...new Set(probes.map((p) => p.platform))], [probes]);
  const filtered = useMemo(() => {
    const list = probes.filter(
      (p) =>
        (stage === "all" || p.stage === stage) && (platform === "all" || p.platform === platform)
    );
    // 扎心的排前面:负面/竞对被推荐你缺席 > 其它
    return [...list].sort((a, b) => Number(isAlert(b)) - Number(isAlert(a)));
  }, [probes, stage, platform]);

  if (probes.length === 0) return null;

  const chip = (active: boolean) =>
    `cursor-pointer rounded-full border px-2.5 py-0.5 text-xs ${
      active ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground"
    }`;

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-sm font-medium">AI 原话回放墙</div>
            <div className="mt-1 text-xs text-muted-foreground">
              红框 = 出现负面 / 竞对被推荐而你缺席 · 共 {probes.length} 条探针
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              className={chip(view === "table")}
              onClick={() => setView(view === "table" ? "cards" : "table")}
              data-testid="geo-probe-view-toggle"
            >
              {view === "table" ? "卡片视图" : "全量明细表"}
            </button>
            <button type="button" className={chip(stage === "all")} onClick={() => setStage("all")}>
              全部阶段
            </button>
            {Object.entries(STAGE_LABELS).map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={chip(stage === k)}
                onClick={() => setStage(k)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <button
            type="button"
            className={chip(platform === "all")}
            onClick={() => setPlatform("all")}
          >
            全部引擎
          </button>
          {platforms.map((p) => (
            <button
              key={p}
              type="button"
              className={chip(platform === p)}
              onClick={() => setPlatform(p)}
            >
              {engineLabel(p)}
            </button>
          ))}
        </div>
        {view === "cards" ? (
          <>
            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {filtered.slice(0, limit).map((p, i) => (
                <ProbeCard key={`${p.platform}-${p.stage}-${i}`} probe={p} />
              ))}
            </div>
            {filtered.length > limit && (
              <div className="mt-4 text-center">
                <Button variant="outline" size="sm" onClick={() => setLimit((n) => n + PAGE * 2)}>
                  展开更多({filtered.length - limit} 条)
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="mt-4 overflow-x-auto" data-testid="geo-probe-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-1.5 pr-2 font-medium">引擎</th>
                  <th className="py-1.5 pr-2 font-medium">阶段</th>
                  <th className="py-1.5 pr-2 font-medium">提问</th>
                  <th className="py-1.5 pr-2 font-medium">提及你</th>
                  <th className="py-1.5 font-medium">回答里提到的品牌</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, i) => (
                  <tr
                    key={`${p.platform}-${i}`}
                    className={`border-b border-border/40 last:border-0 ${
                      isAlert(p) ? "text-rose-500" : ""
                    }`}
                  >
                    <td className="whitespace-nowrap py-1.5 pr-2">{engineLabel(p.platform)}</td>
                    <td className="whitespace-nowrap py-1.5 pr-2">
                      {STAGE_LABELS[p.stage] ?? p.stage}
                    </td>
                    <td className="py-1.5 pr-2">{p.question}</td>
                    <td className="whitespace-nowrap py-1.5 pr-2">
                      {p.mentioned
                        ? `是${typeof p.rank === "number" ? ` · 第${p.rank}位` : ""}`
                        : "否"}
                    </td>
                    <td className="py-1.5 text-muted-foreground">
                      {(p.competitors ?? []).join("、") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
